import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { FIELD_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { Deal } from "../types";

const LEAD_CHOICES = 500;

/**
 * 'Vincular a un lead': picks one of the leads the user can read (RLS scopes a lead_manager to
 * her brazo) and calls `link_whatsapp_conversation` through the provider. The server decides
 * who may link; a refusal is shown in plain Spanish. Refreshes the inbox and the lead badges.
 */
export const InboxLinkDialog = ({
  conversationId,
  open,
  onOpenChange,
}: {
  conversationId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [dealId, setDealId] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const deals = useGetList<Deal>(
    "deals",
    {
      pagination: { page: 1, perPage: LEAD_CHOICES },
      sort: { field: "name", order: "ASC" },
      filter: { "archived_at@is": null },
    },
    { enabled: open },
  );

  const onLink = async () => {
    if (!dealId) return;
    setSaving(true);
    setFailure(null);
    try {
      await dataProvider.linkWhatsAppConversation(conversationId, dealId);
      await queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
      notify("crm.whatsapp.inbox.link_success", { type: "success" });
      onOpenChange(false);
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.whatsapp.inbox.link_error"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.whatsapp.inbox.link_title")}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.whatsapp.inbox.link_help")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="whatsapp-link-deal">
            {translate("crm.whatsapp.inbox.link_label")}
          </Label>
          <select
            id="whatsapp-link-deal"
            className={FIELD_CLASS}
            value={dealId}
            disabled={deals.isPending}
            onChange={(event) => setDealId(event.target.value)}
          >
            <option value="">
              {translate("crm.whatsapp.inbox.link_placeholder")}
            </option>
            {(deals.data ?? []).map((deal) => (
              <option key={String(deal.id)} value={String(deal.id)}>
                {deal.name}
              </option>
            ))}
          </select>
          {deals.error ? (
            <p role="alert" className="text-[15px] text-destructive">
              {translate("crm.whatsapp.inbox.link_leads_error")}
            </p>
          ) : null}
          {failure ? (
            <p role="alert" className="text-[15px] text-destructive">
              {failure}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={() => onOpenChange(false)}
          >
            {translate("crm.whatsapp.inbox.cancel")}
          </Button>
          <Button
            type="button"
            className="h-11"
            disabled={!dealId || saving}
            onClick={onLink}
          >
            {translate("crm.whatsapp.inbox.link_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
